import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Lead, Province, Industry, CompanySize } from './types';
import { PROVINCES, INDUSTRIES, COMPANY_SIZES } from './constants';
import { scoutLeads, generateMockLeads } from './services/geminiService';
import StatsCards from './components/StatsCards';
import LeadTable from './components/LeadTable';
import LeadProfile from './components/LeadProfile';
import SalesAgent from './components/SalesAgent';

const App: React.FC = () => {
  // Persistence Layer
  const [leads, setLeads] = useState<Lead[]>(() => {
    try {
      const saved = localStorage.getItem('sa_lead_scout_crm');
      return saved ? JSON.parse(saved) : [];
    } catch (e) { return []; }
  });

  const [viewMode, setViewMode] = useState<'DASHBOARD' | 'PROFILE' | 'SALES_AGENT'>('DASHBOARD');
  const [listType, setListType] = useState<'SCANNED' | 'CRM'>('SCANNED');
  const [selectedLeadId, setSelectedLeadId] = useState<string | null>(null);
  const [isScouting, setIsScouting] = useState(false);
  const [isOfflineMode, setIsOfflineMode] = useState(false);
  const [notification, setNotification] = useState<{ message: string, type: 'info' | 'error' | 'success' } | null>(null);
  
  // Parameters
  const [currentProvince, setCurrentProvince] = useState<Province>(Province.GAUTENG);
  const [currentIndustry, setCurrentIndustry] = useState<Industry>(Industry.RETAIL);
  const [currentSize, setCurrentSize] = useState<CompanySize>(CompanySize.SMALL);
  const [targetCount, setTargetCount] = useState<number>(6);
  const [mustHaveWhatsApp, setMustHaveWhatsApp] = useState<boolean>(true);
  
  // UI Filtering
  const [showOnlyCatchOfTheDay, setShowOnlyCatchOfTheDay] = useState<boolean>(false);

  useEffect(() => {
    localStorage.setItem('sa_lead_scout_crm', JSON.stringify(leads));
  }, [leads]);

  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => setNotification(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  const filteredLeads = useMemo(() => {
    return leads
      .filter(l => {
        const isMatch = listType === 'SCANNED' ? !l.isArchived : l.isArchived;
        if (!isMatch) return false;
        return !showOnlyCatchOfTheDay || l.isCatchOfTheDay;
      })
      .sort((a, b) => b.vettingScore - a.vettingScore);
  }, [leads, listType, showOnlyCatchOfTheDay]);

  const handleScout = useCallback(async () => {
    if (isScouting) return;
    setIsScouting(true);

    if (isOfflineMode) {
      setNotification({ message: "Cold Forging... Generating leads locally.", type: 'info' });
      setTimeout(() => {
        const mockLeads = generateMockLeads(currentIndustry, currentProvince, targetCount);
        setLeads(prev => [...mockLeads, ...prev]);
        setNotification({ message: `Generated ${targetCount} leads without using AI tokens.`, type: 'success' });
        setIsScouting(false);
      }, 800);
      return;
    }

    setNotification({ message: "Igniting furnace... Contacting Gemini API.", type: 'info' });
    try {
      const newLeads = await scoutLeads(
        currentIndustry, 
        currentProvince, 
        currentSize, 
        targetCount, 
        "Standard Transformation", 
        false, 
        undefined, 
        mustHaveWhatsApp
      );
      setLeads(prev => [...newLeads, ...prev]);
      setNotification({ message: `Successfully forged ${newLeads.length} leads.`, type: 'success' });
    } catch (err: any) {
      setNotification({ message: err.message, type: 'error' });
    } finally {
      setIsScouting(false);
    }
  }, [isScouting, isOfflineMode, currentIndustry, currentProvince, currentSize, targetCount, mustHaveWhatsApp]);

  const handleToggleArchive = (id: string) => {
    setLeads(prev => prev.map(l => l.id === id ? { ...l, isArchived: !l.isArchived } : l));
    const lead = leads.find(l => l.id === id);
    setNotification({ 
      message: lead?.isArchived ? "Lead returned to Furnace." : "Lead secured in The Vault.", 
      type: 'success' 
    });
  };

  const handleAddManual = () => {
    const name = prompt("Business Name:");
    const web = prompt("Website (or NONE):");
    if (!name) return;
    
    const newLead: Lead = {
      id: Math.random().toString(36).substr(2, 9),
      dateScraped: new Date().toISOString().split('T')[0],
      businessName: name,
      industry: currentIndustry,
      province: currentProvince,
      companySize: currentSize,
      website: web || "NONE",
      contactNumber: "Manual Entry",
      email: "Manual Entry",
      vettingScore: 5,
      status: 'Vetted',
      notes: "Manually entered lead.",
      isCatchOfTheDay: (web === "NONE" || !web),
      isArchived: false,
      activityLog: [],
    };
    setLeads(prev => [newLead, ...prev]);
    setNotification({ message: "Manual lead added to furnace.", type: 'success' });
  };

  const activeCount = leads.filter(l => !l.isArchived).length;
  const vaultCount = leads.filter(l => l.isArchived).length;
  const catchCount = filteredLeads.filter(l => l.isCatchOfTheDay).length;

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      {notification && (
        <div className="fixed top-24 right-6 z-50 animate-in slide-in-from-right duration-300">
          <div className={`px-6 py-4 rounded-xl shadow-2xl flex items-center gap-4 border ${notification.type === 'error' ? 'bg-red-600 border-red-700 text-white' : 'bg-black border-gray-800 text-white'}`}>
            <i className={`fa-solid ${notification.type === 'error' ? 'fa-triangle-exclamation' : 'fa-fire'}`}></i>
            <p className="text-[10px] font-black uppercase tracking-widest">{notification.message}</p>
          </div>
        </div>
      )}

      <header className="bg-black border-b border-red-900/20 sticky top-0 z-30 py-4 shadow-2xl">
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
          <div className="flex items-center gap-8">
            <div className="flex items-center gap-4 cursor-pointer" onClick={() => { setViewMode('DASHBOARD'); setListType('SCANNED'); }}>
              <div className="w-10 h-10 bg-red-600 text-white rounded flex items-center justify-center font-black italic text-xl shadow-[0_0_20px_rgba(220,38,38,0.4)] transition-transform hover:scale-110">RIF</div>
              <div className="hidden sm:block">
                <h1 className="font-black text-lg text-white tracking-tighter uppercase italic leading-none">Reel Impact Forged</h1>
                <p className="text-[9px] text-red-500 font-black uppercase tracking-[0.2em] mt-1">SOUTH AFRICA LEAD SCOUT</p>
              </div>
            </div>

            <nav className="flex items-center gap-1 bg-gray-900/50 p-1 rounded-lg">
              <button 
                onClick={() => { setListType('SCANNED'); setViewMode('DASHBOARD'); }}
                className={`px-4 py-2 text-[10px] font-black uppercase tracking-widest rounded transition-all ${listType === 'SCANNED' && viewMode !== 'SALES_AGENT' ? 'bg-red-600 text-white' : 'text-gray-400 hover:text-white'}`}>
                Furnace <span className="ml-1 opacity-50">({activeCount})</span>
              </button>
              <button 
                onClick={() => { setListType('CRM'); setViewMode('DASHBOARD'); }}
                className={`px-4 py-2 text-[10px] font-black uppercase tracking-widest rounded transition-all ${listType === 'CRM' && viewMode !== 'SALES_AGENT' ? 'bg-red-600 text-white' : 'text-gray-400 hover:text-white'}`}>
                The Vault <span className="ml-1 opacity-50">({vaultCount})</span>
              </button>
              <button 
                onClick={() => setViewMode('SALES_AGENT')}
                className={`px-4 py-2 text-[10px] font-black uppercase tracking-widest rounded transition-all ${viewMode === 'SALES_AGENT' ? 'bg-red-600 text-white' : 'text-gray-400 hover:text-white'}`}>
                Striker
              </button>
            </nav>
          </div>

          <div className="flex items-center gap-4">
            <button onClick={handleScout} disabled={isScouting}
               className="bg-red-600 text-white px-8 py-2.5 rounded font-black text-xs uppercase tracking-widest hover:bg-red-700 transition-all shadow-xl disabled:opacity-50 group">
               {isScouting ? 'FORGING...' : 'STRIKE'}
               <i className="fa-solid fa-bolt ml-2 group-hover:animate-pulse"></i>
            </button>
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-7xl mx-auto w-full p-8">
        {viewMode === 'DASHBOARD' ? (
          <>
            <StatsCards 
              totalLeads={leads.length} 
              vettedCount={listType === 'SCANNED' ? activeCount : vaultCount} 
              catchOfTheDayCount={catchCount} 
              discardedCount={0} 
              isResting={isOfflineMode} 
            />
            
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
              <div className="lg:col-span-3">
                <LeadTable 
                  leads={filteredLeads} 
                  filterSize="All" onFilterSizeChange={() => {}}
                  filterScore="All" onFilterScoreChange={() => {}}
                  filterStatus="All" onFilterStatusChange={() => {}}
                  filterFacebook="All" onFilterFacebookChange={() => {}}
                  showOnlyCatchOfTheDay={showOnlyCatchOfTheDay} onToggleCatchOfTheDay={setShowOnlyCatchOfTheDay}
                  onSelectLead={(l) => { setSelectedLeadId(l.id); setViewMode('PROFILE'); }}
                />
              </div>

              <aside className="space-y-6">
                <div className="bg-white p-6 rounded-xl border-t-4 border-black shadow-xl sticky top-28">
                  <h3 className="font-black text-xs uppercase tracking-widest mb-6 flex items-center gap-2">
                    <i className="fa-solid fa-fire-burner text-red-600"></i> Ignition Settings
                  </h3>
                  
                  <div className="space-y-6">
                    <div>
                      <label className="block text-[9px] font-black text-gray-400 uppercase mb-2">Province</label>
                      <select value={currentProvince} onChange={(e) => setCurrentProvince(e.target.value as Province)} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-xs font-bold outline-none focus:border-red-600 transition-colors">
                        {PROVINCES.map(p => <option key={p} value={p}>{p}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-[9px] font-black text-gray-400 uppercase mb-2">Industry</label>
                      <select value={currentIndustry} onChange={(e) => setCurrentIndustry(e.target.value as Industry)} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-xs font-bold outline-none focus:border-red-600 transition-colors">
                        {INDUSTRIES.map(i => <option key={i} value={i}>{i}</option>)}
                      </select>
                    </div>
                    
                    {/* COMPANY SIZE DROPDOWN */}
                    <div>
                      <label className="block text-[9px] font-black text-gray-400 uppercase mb-2">Target Business Size</label>
                      <select value={currentSize} onChange={(e) => setCurrentSize(e.target.value as CompanySize)} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-xs font-bold outline-none focus:border-red-600 transition-colors">
                        {COMPANY_SIZES.map(size => <option key={size} value={size}>{size}</option>)}
                      </select>
                    </div>

                    <div className="flex items-center gap-3 pt-2">
                      <input 
                        type="checkbox" 
                        id="wa_req" 
                        checked={mustHaveWhatsApp}
                        onChange={(e) => setMustHaveWhatsApp(e.target.checked)}
                        className="accent-red-600 w-4 h-4" 
                      />
                      <label htmlFor="wa_req" className="text-[10px] font-black uppercase text-gray-700 cursor-pointer">WhatsApp Required</label>
                    </div>

                    <div className="pt-4 border-t border-gray-100">
                       <button onClick={handleScout} disabled={isScouting} className="w-full py-3 bg-black text-white rounded-lg text-[10px] font-black uppercase tracking-widest hover:bg-gray-900 transition-all flex items-center justify-center gap-2">
                        {isScouting ? 'IGNITING...' : (
                          <>
                            <i className="fa-solid fa-microchip"></i> Start Batch Forge
                          </>
                        )}
                       </button>
                    </div>

                    <div className="flex items-center justify-between p-3 bg-blue-50/50 rounded-lg border border-blue-100 group transition-all">
                      <div className="flex items-center gap-3">
                        <i className={`fa-solid fa-snowflake ${isOfflineMode ? 'text-blue-600' : 'text-gray-400'}`}></i>
                        <span className="text-[10px] font-black uppercase text-gray-700">Cold Forge</span>
                      </div>
                      <button onClick={() => setIsOfflineMode(!isOfflineMode)}
                        className={`w-10 h-5 rounded-full relative transition-all ${isOfflineMode ? 'bg-blue-600' : 'bg-gray-300'}`}>
                        <div className={`absolute top-1 w-3 h-3 bg-white rounded-full transition-all ${isOfflineMode ? 'right-1' : 'left-1'}`}></div>
                      </button>
                    </div>
                  </div>
                </div>
              </aside>
            </div>
          </>
        ) : viewMode === 'PROFILE' ? (
          <LeadProfile 
            lead={leads.find(l => l.id === selectedLeadId)!} 
            onBack={() => setViewMode('DASHBOARD')} 
            onAddNote={(lid, text) => setLeads(prev => prev.map(l => l.id === lid ? { ...l, activityLog: [...(l.activityLog || []), { id: Math.random().toString(), date: new Date().toISOString(), text }] } : l))}
            onDeleteNote={(lid, nid) => setLeads(prev => prev.map(l => l.id === lid ? { ...l, activityLog: l.activityLog.filter(n => n.id !== nid) } : l))}
            onValidate={() => {}} 
            onMarkInterested={() => { handleToggleArchive(selectedLeadId!); setViewMode('DASHBOARD'); }} 
          />
        ) : (
          <SalesAgent leads={leads} onAddNote={() => {}} />
        )}
      </main>
    </div>
  );
};

export default App;